import { Button, cn } from "@simpro/ui";
import { RotateCw } from "lucide-react";
import type { ReactNode } from "react";
import { pill } from "@/shared/styles/pill";

export interface ProblemCardProps {
  title: string;
  description?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  /** In place of Retry, when the way forward is somewhere else. */
  action?: ReactNode;
  headingLevel?: 1 | 2 | 3;
  /** `refusal`: the salon said no, rather than something failing; it leads with a ringed "!" (F3). */
  tone?: "problem" | "refusal";
  className?: string;
}

/**
 * Something went wrong, and the one thing to do about it (design note S2): never a red card, so a
 * failed section reads as a hiccup rather than an alarm. A home-page section that fails is left out
 * entirely; this card is for what the customer came to see. A refusal (follow-up F3) is the same card
 * led by a ringed "!", never red.
 *
 * Restyled under design note salon-booking-premium-redesign.md section 5.3 item 16: a hairline card
 * with an ink rule down its leading edge, the title in the display face, Retry an ink pill.
 */
export function ProblemCard({
  title,
  description,
  onRetry,
  retryLabel = "Retry",
  action,
  headingLevel = 2,
  tone = "problem",
  className,
}: ProblemCardProps) {
  const Heading = `h${headingLevel}` as const;

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-start gap-2.5 rounded-card border border-l-2 border-edge-subtle border-l-heading bg-surface p-5 sm:p-6",
        className,
      )}
    >
      {tone === "refusal" ? (
        <span
          aria-hidden="true"
          className="headline mb-1 grid size-11 place-items-center rounded-full text-lg text-heading ring-1 ring-edge ring-inset"
        >
          !
        </span>
      ) : null}
      <Heading className="headline text-xl leading-snug text-heading">{title}</Heading>
      {description ? <p className="text-sm leading-relaxed text-subtle">{description}</p> : null}
      {onRetry ? (
        <Button onClick={onRetry} className={cn(pill.base, pill.ink, "mt-2 h-11 px-5 text-sm")}>
          <RotateCw className="size-4" aria-hidden="true" />
          {retryLabel}
        </Button>
      ) : (
        action
      )}
    </div>
  );
}
